export type PollType = 'single' | 'multiple' | 'quick' | 'ranking' | 'elimination';

export type PollLocation = 'cabina' | 'radio' | 'programa' | 'locutor' | 'club' | 'evento';

export type PollStatus = 'active' | 'closed' | 'scheduled';

export interface PollOption {
  id: string;
  text: string;
  votes: number;
  percentage: number;
}

export interface PollScore {
  createPoll: number;
  vote: number;
  share: number;
  comment: number;
}

export interface Poll {
  id: string;
  question: string;
  description: string;
  type: PollType;
  location: PollLocation;
  locationName: string;
  entityName: string;
  entityId: string;
  entityLogoUrl: string;
  status: PollStatus;
  createdAt: string;
  closesAt: string;
  createdBy: string;
  options: PollOption[];
  totalVotes: number;
  participants: number;
  comments: number;
  shares: number;
  score: PollScore;
  winnerId: string | null;
  tags: string[];
}

export const pollTypeLabels: Record<PollType, string> = {
  single: 'Una Respuesta',
  multiple: 'Múltiples Respuestas',
  quick: 'Votación Rápida',
  ranking: 'Ranking',
  elimination: 'Eliminación',
};

export const pollTypeIcons: Record<PollType, string> = {
  single: 'ri-radio-button-line',
  multiple: 'ri-checkbox-multiple-line',
  quick: 'ri-flashlight-line',
  ranking: 'ri-bar-chart-horizontal-line',
  elimination: 'ri-close-circle-line',
};

export const pollTypeColors: Record<PollType, string> = {
  single: 'bg-sky-50 text-sky-700 border-sky-200',
  multiple: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  quick: 'bg-amber-50 text-amber-700 border-amber-200',
  ranking: 'bg-violet-50 text-violet-700 border-violet-200',
  elimination: 'bg-rose-50 text-rose-700 border-rose-200',
};

export const pollTypeBgLight: Record<PollType, string> = {
  single: 'bg-sky-50',
  multiple: 'bg-emerald-50',
  quick: 'bg-amber-50',
  ranking: 'bg-violet-50',
  elimination: 'bg-rose-50',
};

export const pollTypeDotColors: Record<PollType, string> = {
  single: 'bg-sky-500',
  multiple: 'bg-emerald-500',
  quick: 'bg-amber-500',
  ranking: 'bg-violet-500',
  elimination: 'bg-rose-500',
};

export const pollTypeTextColors: Record<PollType, string> = {
  single: 'text-sky-700',
  multiple: 'text-emerald-700',
  quick: 'text-amber-700',
  ranking: 'text-violet-700',
  elimination: 'text-rose-700',
};

export const pollLocationLabels: Record<PollLocation, string> = {
  cabina: 'Cabina',
  radio: 'Radio',
  programa: 'Programa',
  locutor: 'Locutor',
  club: 'Club',
  evento: 'Evento',
};

export const pollLocationIcons: Record<PollLocation, string> = {
  cabina: 'ri-chat-smile-2-line',
  radio: 'ri-radio-line',
  programa: 'ri-tv-line',
  locutor: 'ri-user-voice-line',
  club: 'ri-shield-star-line',
  evento: 'ri-calendar-event-line',
};

export const pollLocationColors: Record<PollLocation, string> = {
  cabina: 'bg-red-50 text-red-700 border-red-200',
  radio: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  programa: 'bg-teal-50 text-teal-700 border-teal-200',
  locutor: 'bg-amber-50 text-amber-700 border-amber-200',
  club: 'bg-violet-50 text-violet-700 border-violet-200',
  evento: 'bg-orange-50 text-orange-700 border-orange-200',
};

export const pollStatusLabels: Record<PollStatus, string> = {
  active: 'Activa',
  closed: 'Cerrada',
  scheduled: 'Programada',
};

export const pollDashboardStats = {
  totalPolls: 347,
  activeNow: 12,
  todayCreated: 8,
  totalVotes: 892400,
  totalParticipants: 156200,
  totalComments: 48700,
  totalShares: 123500,
  avgEngagement: 68.4,
  topLocation: 'cabina' as PollLocation,
  activeInCabina: 4,
  closingSoon: 5,
};

export const rfwPolls: Poll[] = [
  {
    id: 'poll-001',
    question: '¿Quién es el mejor locutor de la radio esta semana?',
    description: 'Votá por el locutor que más te entretuvo esta semana. El ganador recibe una insignia especial y reconocimiento en vivo.',
    type: 'single',
    location: 'cabina',
    locationName: 'Cabina',
    entityName: 'Radio Nueva Actitud',
    entityId: 'radio-94.70',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T08:00:00Z',
    closesAt: '2026-06-22T20:00:00Z',
    createdBy: 'Radio Nueva Actitud',
    options: [
      { id: 'opt-001a', text: 'Valentina Ríos', votes: 342, percentage: 44.2 },
      { id: 'opt-001b', text: 'Carlos Mendoza', votes: 231, percentage: 29.8 },
      { id: 'opt-001c', text: 'Ana Laura Vega', votes: 125, percentage: 16.1 },
      { id: 'opt-001d', text: 'Diego Quintero', votes: 76, percentage: 9.8 },
    ],
    totalVotes: 774,
    participants: 774,
    comments: 89,
    shares: 45,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['locutor', 'cabina', 'semanal'],
  },
  {
    id: 'poll-002',
    question: '¿Qué canción dedicamos esta noche? Elegí el próximo tema',
    description: 'Los oyentes de la cabina eligen el próximo tema que sonará al aire. La canción más votada se reproducirá con dedicatoria en vivo.',
    type: 'quick',
    location: 'cabina',
    locationName: 'Cabina',
    entityName: 'Radio Nueva Actitud',
    entityId: 'radio-94.70',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T18:00:00Z',
    closesAt: '2026-06-21T19:30:00Z',
    createdBy: 'Valentina Ríos',
    options: [
      { id: 'opt-002a', text: 'Flowers - Miley Cyrus', votes: 89, percentage: 38.2 },
      { id: 'opt-002b', text: 'As It Was - Harry Styles', votes: 67, percentage: 28.8 },
      { id: 'opt-002c', text: 'Anti-Hero - Taylor Swift', votes: 45, percentage: 19.3 },
      { id: 'opt-002d', text: 'Calm Down - Rema & Selena', votes: 32, percentage: 13.7 },
    ],
    totalVotes: 233,
    participants: 233,
    comments: 56,
    shares: 12,
    score: { createPoll: 100, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['música', 'cabina', 'dedicatoria'],
  },
  {
    id: 'poll-003',
    question: '¿Cuáles son tus 3 géneros musicales favoritos?',
    description: 'Seleccioná hasta 3 géneros que más escuchás en tu día a día. Los resultados ayudarán a programar mejor la música de la radio.',
    type: 'multiple',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'Rock Total Radio',
    entityId: 'radio-87.01',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-20T10:00:00Z',
    closesAt: '2026-06-27T23:59:00Z',
    createdBy: 'Rock Total Radio',
    options: [
      { id: 'opt-003a', text: 'Rock Clásico', votes: 456, percentage: 72.5 },
      { id: 'opt-003b', text: 'Metal', votes: 389, percentage: 61.8 },
      { id: 'opt-003c', text: 'Alternativo', votes: 312, percentage: 49.6 },
      { id: 'opt-003d', text: 'Punk', votes: 198, percentage: 31.4 },
      { id: 'opt-003e', text: 'Indie Rock', votes: 178, percentage: 28.3 },
      { id: 'opt-003f', text: 'Grunge', votes: 145, percentage: 23.0 },
    ],
    totalVotes: 629,
    participants: 629,
    comments: 234,
    shares: 68,
    score: { createPoll: 200, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['música', 'radio', 'programación'],
  },
  {
    id: 'poll-004',
    question: 'Ranking: Las mejores bandas de rock de todos los tiempos',
    description: 'Armá tu ranking personal de las bandas más legendarias del rock. Cada voto define las posiciones en el ranking definitivo de Rock Total Radio.',
    type: 'ranking',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'Rock Total Radio',
    entityId: 'radio-87.01',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-18T12:00:00Z',
    closesAt: '2026-06-30T23:59:00Z',
    createdBy: 'Rock Total Radio',
    options: [
      { id: 'opt-004a', text: 'Led Zeppelin', votes: 892, percentage: 85.6 },
      { id: 'opt-004b', text: 'Queen', votes: 845, percentage: 81.0 },
      { id: 'opt-004c', text: 'Pink Floyd', votes: 798, percentage: 76.5 },
      { id: 'opt-004d', text: 'The Beatles', votes: 756, percentage: 72.5 },
      { id: 'opt-004e', text: 'Metallica', votes: 712, percentage: 68.3 },
      { id: 'opt-004f', text: 'Nirvana', votes: 598, percentage: 57.4 },
      { id: 'opt-004g', text: 'AC/DC', votes: 567, percentage: 54.4 },
      { id: 'opt-004h', text: 'Guns N Roses', votes: 534, percentage: 51.2 },
    ],
    totalVotes: 1043,
    participants: 1043,
    comments: 567,
    shares: 234,
    score: { createPoll: 300, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['música', 'ranking', 'rock'],
  },
  {
    id: 'poll-005',
    question: 'Eliminación: El mejor programa de la semana',
    description: 'Cada hora eliminamos al programa con menos votos. El último que quede en pie gana el título de Programa de la Semana y un espacio destacado en la landing.',
    type: 'elimination',
    location: 'programa',
    locationName: 'Programa',
    entityName: 'Carrusel Deportivo',
    entityId: 'program-carrusel-deportivo',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20program%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20microphone%2C%20dynamic%20energetic%20style%2C%20bold%20red%20tones&width=100&height=100&seq=logo-carrusel-deportivo&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T09:00:00Z',
    closesAt: '2026-06-21T23:00:00Z',
    createdBy: 'Carrusel Deportivo',
    options: [
      { id: 'opt-005a', text: 'Carrusel Deportivo', votes: 278, percentage: 34.8 },
      { id: 'opt-005b', text: 'Deporte Total', votes: 245, percentage: 30.6 },
      { id: 'opt-005c', text: 'Mañanas Argentinas', votes: 167, percentage: 20.9 },
      { id: 'opt-005d', text: 'Onda Nocturna', votes: 110, percentage: 13.8 },
    ],
    totalVotes: 800,
    participants: 800,
    comments: 345,
    shares: 178,
    score: { createPoll: 250, vote: 15, share: 20, comment: 5 },
    winnerId: null,
    tags: ['programa', 'eliminación', 'semanal'],
  },
  {
    id: 'poll-006',
    question: '¿A qué locutor querés escuchar en una entrevista exclusiva?',
    description: 'El locutor más votado dará una entrevista de 30 minutos en vivo respondiendo preguntas de los oyentes. ¡Votá ya!',
    type: 'single',
    location: 'locutor',
    locationName: 'Locutor',
    entityName: 'Banda Radio MX',
    entityId: 'radio-200.00',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=100&height=100&seq=logo-banda-mx&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-20T14:00:00Z',
    closesAt: '2026-06-24T18:00:00Z',
    createdBy: 'Banda Radio MX',
    options: [
      { id: 'opt-006a', text: 'Ricky Navarro', votes: 567, percentage: 51.5 },
      { id: 'opt-006b', text: 'Javier Mendoza', votes: 234, percentage: 21.3 },
      { id: 'opt-006c', text: 'Sofía Linares', votes: 198, percentage: 18.0 },
      { id: 'opt-006d', text: 'Felipe Gutiérrez', votes: 101, percentage: 9.2 },
    ],
    totalVotes: 1100,
    participants: 1100,
    comments: 156,
    shares: 89,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['locutor', 'entrevista', 'exclusivo'],
  },
  {
    id: 'poll-007',
    question: '¿Qué tipo de reto querés para el club esta semana?',
    description: 'Los miembros del club eligen el próximo reto comunitario. El reto ganador se activa mañana y los mejores participantes reciben insignias.',
    type: 'single',
    location: 'club',
    locationName: 'Club',
    entityName: 'Club Oficial La Mexicana FM',
    entityId: 'club-003',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T07:00:00Z',
    closesAt: '2026-06-22T07:00:00Z',
    createdBy: 'Club Oficial La Mexicana FM',
    options: [
      { id: 'opt-007a', text: 'Trivia de Cultura Mexicana', votes: 98, percentage: 42.2 },
      { id: 'opt-007b', text: 'Karaoke Virtual en Grupo', votes: 67, percentage: 28.9 },
      { id: 'opt-007c', text: 'Adivina la Canción', votes: 45, percentage: 19.4 },
      { id: 'opt-007d', text: 'Reto de Fotografía', votes: 22, percentage: 9.5 },
    ],
    totalVotes: 232,
    participants: 232,
    comments: 78,
    shares: 34,
    score: { createPoll: 100, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['club', 'reto', 'comunidad'],
  },
  {
    id: 'poll-008',
    question: '¿A qué hora preferís el Meet & Greet con los locutores?',
    description: 'Ayudanos a elegir el mejor horario para el evento especial Meet & Greet. ¡Votá y asegurate de poder asistir!',
    type: 'quick',
    location: 'evento',
    locationName: 'Evento',
    entityName: 'Meet & Greet Virtual con Locutores',
    entityId: 'evt-016',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Virtual%20video%20call%20grid%20with%20multiple%20radio%20hosts%20on%20screen%2C%20friendly%20engaging%20atmosphere%2C%20casual%20studio%20backgrounds%2C%20community%20interaction%2C%20warm%20modern%20video%20conferencing%20photography&width=100&height=100&seq=logo-meet-greet-virtual&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T10:00:00Z',
    closesAt: '2026-06-25T10:00:00Z',
    createdBy: 'Radio Voz del Pueblo',
    options: [
      { id: 'opt-008a', text: 'Mañana (10 AM - 12 PM)', votes: 156, percentage: 25.7 },
      { id: 'opt-008b', text: 'Tarde (4 PM - 6 PM)', votes: 234, percentage: 38.6 },
      { id: 'opt-008c', text: 'Noche (7 PM - 9 PM)', votes: 189, percentage: 31.1 },
      { id: 'opt-008d', text: 'Fin de Semana (Sábado)', votes: 28, percentage: 4.6 },
    ],
    totalVotes: 607,
    participants: 607,
    comments: 89,
    shares: 45,
    score: { createPoll: 100, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['evento', 'horario', 'comunidad'],
  },
  {
    id: 'poll-009',
    question: '¿Qué tema debería tratar el próximo episodio de Deporte Total?',
    description: 'Los seguidores del programa deciden el contenido. El tema más votado será el tema central del episodio del próximo lunes.',
    type: 'single',
    location: 'programa',
    locationName: 'Programa',
    entityName: 'Deporte Total',
    entityId: 'program-deporte-total',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20program%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20microphone%2C%20dynamic%20energetic%20style%2C%20bold%20red%20tones&width=100&height=100&seq=logo-deporte-total&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T06:00:00Z',
    closesAt: '2026-06-23T18:00:00Z',
    createdBy: 'Deporte Total',
    options: [
      { id: 'opt-009a', text: 'Fútbol: Análisis de la Temporada', votes: 345, percentage: 41.5 },
      { id: 'opt-009b', text: 'Boxeo: Las Peleas del Año', votes: 198, percentage: 23.8 },
      { id: 'opt-009c', text: 'Béisbol: Carrera por el Título', votes: 167, percentage: 20.0 },
      { id: 'opt-009d', text: 'Automovilismo: F1 y NASCAR', votes: 123, percentage: 14.8 },
    ],
    totalVotes: 833,
    participants: 833,
    comments: 210,
    shares: 67,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['programa', 'deportes', 'contenido'],
  },
  {
    id: 'poll-010',
    question: 'Votación rápida: ¿Equipo favorito para ganar el torneo?',
    description: 'Votación express de 2 horas. Solo una opción. El resultado se anuncia en vivo en Carrusel Deportivo. ¡No te quedes afuera!',
    type: 'quick',
    location: 'cabina',
    locationName: 'Cabina',
    entityName: 'Deportes Total Radio',
    entityId: 'radio-120.00',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20star%20motifs%2C%20bold%20energetic%20colors%2C%20dynamic%20sports%20brand%20identity&width=100&height=100&seq=logo-deportes-total&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T16:00:00Z',
    closesAt: '2026-06-21T18:00:00Z',
    createdBy: 'Fernando Gutiérrez',
    options: [
      { id: 'opt-010a', text: 'Águilas del América', votes: 445, percentage: 38.9 },
      { id: 'opt-010b', text: 'Chivas Rayadas', votes: 312, percentage: 27.3 },
      { id: 'opt-010c', text: 'Cruz Azul', votes: 234, percentage: 20.5 },
      { id: 'opt-010d', text: 'Tigres UANL', votes: 152, percentage: 13.3 },
    ],
    totalVotes: 1143,
    participants: 1143,
    comments: 567,
    shares: 234,
    score: { createPoll: 100, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['deportes', 'cabina', 'rápida'],
  },
  {
    id: 'poll-011',
    question: '¿Qué radio merece ser la Radio del Mes?',
    description: 'Votá por la radio que más te hizo vibrar este mes. La ganadora recibe una insignia de Radio del Mes y exposición en toda la landing.',
    type: 'single',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'Radio Nueva Actitud',
    entityId: 'radio-94.70',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-15T00:00:00Z',
    closesAt: '2026-06-30T23:59:00Z',
    createdBy: 'RFW Central',
    options: [
      { id: 'opt-011a', text: 'Radio Nueva Actitud', votes: 1234, percentage: 28.9 },
      { id: 'opt-011b', text: 'La Mexicana FM', votes: 1102, percentage: 25.8 },
      { id: 'opt-011c', text: 'Rock Total Radio', votes: 987, percentage: 23.1 },
      { id: 'opt-011d', text: 'Banda Radio MX', votes: 876, percentage: 20.5 },
      { id: 'opt-011e', text: 'Deportes Total Radio', votes: 70, percentage: 1.6 },
    ],
    totalVotes: 4269,
    participants: 4269,
    comments: 890,
    shares: 456,
    score: { createPoll: 350, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['radio', 'mensual', 'ranking'],
  },
  {
    id: 'poll-012',
    question: 'Elegí las 3 mejores frases del locutor esta semana',
    description: 'Cada locutor dijo frases memorables al aire. Seleccioná las 3 que más te hicieron reír, pensar o emocionarte.',
    type: 'multiple',
    location: 'locutor',
    locationName: 'Locutor',
    entityName: 'Ricky Navarro',
    entityId: 'host-ricky-navarro',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20radio%20host%20portrait%2C%20warm%20smile%2C%20professional%20studio%20setting%2C%20golden%20lighting%2C%20clean%20background%2C%20editorial%20photography&width=100&height=100&seq=logo-ricky-navarro&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-20T20:00:00Z',
    closesAt: '2026-06-22T20:00:00Z',
    createdBy: 'Ricky Navarro',
    options: [
      { id: 'opt-012a', text: '"La música es el idioma que todos entendemos"', votes: 567, percentage: 82.4 },
      { id: 'opt-012b', text: '"Hoy es viernes y el cuerpo lo sabe"', votes: 498, percentage: 72.4 },
      { id: 'opt-012c', text: '"No apagues la radio que esto se pone bueno"', votes: 423, percentage: 61.5 },
      { id: 'opt-012d', text: '"Un aplauso para el oyente que madrugó"', votes: 312, percentage: 45.3 },
      { id: 'opt-012e', text: '"La vida es una playlist, ponela en shuffle"', votes: 278, percentage: 40.4 },
    ],
    totalVotes: 688,
    participants: 688,
    comments: 234,
    shares: 89,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['locutor', 'frases', 'diversión'],
  },
  {
    id: 'poll-013',
    question: 'Ranking: Los mejores momentos del Club este mes',
    description: 'Ordená de mejor a peor los momentos más épicos del club. El ranking final se publica en el muro del club y los top 3 reciben puntos extra.',
    type: 'ranking',
    location: 'club',
    locationName: 'Club',
    entityName: 'Club Oficial Rock Total Radio',
    entityId: 'club-005',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-19T10:00:00Z',
    closesAt: '2026-06-28T23:59:00Z',
    createdBy: 'Club Oficial Rock Total Radio',
    options: [
      { id: 'opt-013a', text: 'Trivia de Rock: 200+ participantes', votes: 234, percentage: 78.3 },
      { id: 'opt-013b', text: 'Escuchada Grupal de Master of Puppets', votes: 198, percentage: 66.2 },
      { id: 'opt-013c', text: 'Karaoke Virtual de Clásicos', votes: 167, percentage: 55.9 },
      { id: 'opt-013d', text: 'Duelo de Bandas Tributo', votes: 145, percentage: 48.5 },
      { id: 'opt-013e', text: 'Sorteo de Vinilos Autografiados', votes: 123, percentage: 41.1 },
    ],
    totalVotes: 299,
    participants: 299,
    comments: 145,
    shares: 56,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['club', 'ranking', 'momentos'],
  },
  {
    id: 'poll-014',
    question: '¿Qué tipo de concurso querés que organicemos próximamente?',
    description: 'La comunidad elige el formato del próximo concurso. Votá y participá en el diseño de los concursos de tu radio favorita.',
    type: 'single',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'La Kalle 99.9',
    entityId: 'radio-99.90',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=100&height=100&seq=logo-la-kalle&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-20T15:00:00Z',
    closesAt: '2026-06-26T15:00:00Z',
    createdBy: 'La Kalle 99.9',
    options: [
      { id: 'opt-014a', text: 'Concurso de Canto', votes: 234, percentage: 35.7 },
      { id: 'opt-014b', text: 'Trivia de Cultura Pop', votes: 189, percentage: 28.8 },
      { id: 'opt-014c', text: 'Batalla de Baile Virtual', votes: 145, percentage: 22.1 },
      { id: 'opt-014d', text: 'Concurso de Stand-Up', votes: 88, percentage: 13.4 },
    ],
    totalVotes: 656,
    participants: 656,
    comments: 178,
    shares: 67,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['radio', 'concurso', 'comunidad'],
  },
  {
    id: 'poll-015',
    question: 'Eliminación: El personaje más icónico de la radio',
    description: 'Cada 3 horas eliminamos al personaje con menos votos. El último en pie gana el título de Personaje Más Icónico de la Temporada. ¡Defendé a tu favorito!',
    type: 'elimination',
    location: 'club',
    locationName: 'Club',
    entityName: 'Club VIP Banda Radio MX',
    entityId: 'club-012',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=100&height=100&seq=logo-banda-mx&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T12:00:00Z',
    closesAt: '2026-06-24T12:00:00Z',
    createdBy: 'Club VIP Banda Radio MX',
    options: [
      { id: 'opt-015a', text: 'El Comediante del Micro', votes: 345, percentage: 32.1 },
      { id: 'opt-015b', text: 'La Voz Misteriosa', votes: 278, percentage: 25.9 },
      { id: 'opt-015c', text: 'El Fan Número 1', votes: 234, percentage: 21.8 },
      { id: 'opt-015d', text: 'El Sabio de la Semana', votes: 156, percentage: 14.5 },
      { id: 'opt-015e', text: 'La Reina del Chat', votes: 61, percentage: 5.7 },
    ],
    totalVotes: 1074,
    participants: 1074,
    comments: 478,
    shares: 189,
    score: { createPoll: 200, vote: 15, share: 20, comment: 5 },
    winnerId: null,
    tags: ['club', 'eliminación', 'diversión'],
  },
  {
    id: 'poll-016',
    question: '¿Qué evento te gustaría que organicemos en tu ciudad?',
    description: 'Estamos planeando eventos en nuevas ciudades. Votá por tu ciudad y tipo de evento para ayudarnos a decidir dónde y qué organizar.',
    type: 'single',
    location: 'evento',
    locationName: 'Evento',
    entityName: 'RFW Eventos',
    entityId: 'rfw-events-central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20event%20festival%20scene%20with%20multiple%20stages%20colorful%20lights%20and%20huge%20energetic%20crowd%2C%20concert%20photography%20with%20warm%20festival%20energy&width=100&height=100&seq=logo-rfw-eventos&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-18T08:00:00Z',
    closesAt: '2026-07-02T23:59:00Z',
    createdBy: 'RFW Central',
    options: [
      { id: 'opt-016a', text: 'Concierto Masivo en Houston', votes: 456, percentage: 22.5 },
      { id: 'opt-016b', text: 'Festival en Los Ángeles', votes: 398, percentage: 19.6 },
      { id: 'opt-016c', text: 'Baile de Banda en San Antonio', votes: 345, percentage: 17.0 },
      { id: 'opt-016d', text: 'Feria Multicultural en Chicago', votes: 312, percentage: 15.4 },
      { id: 'opt-016e', text: 'Noche de Rock en Dallas', votes: 278, percentage: 13.7 },
      { id: 'opt-016f', text: 'Festival Urbano en Phoenix', votes: 240, percentage: 11.8 },
    ],
    totalVotes: 2029,
    participants: 2029,
    comments: 567,
    shares: 234,
    score: { createPoll: 300, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['evento', 'ciudades', 'planificación'],
  },
  {
    id: 'poll-017',
    question: '¿Cuál fue la entrevista más impactante del mes?',
    description: 'Reviví las mejores entrevistas del mes y votá por la que más te marcó. Los resultados determinan cuál se repite en un especial de fin de mes.',
    type: 'single',
    location: 'programa',
    locationName: 'Programa',
    entityName: 'Noticias 24/7 Radio',
    entityId: 'radio-107.90',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20microphone%20motifs%2C%20professional%20tones%2C%20authoritative%20news%20brand%20identity%2C%20clean%20modern%20aesthetic&width=100&height=100&seq=logo-noticias-247&orientation=squarish',
    status: 'closed',
    createdAt: '2026-06-10T10:00:00Z',
    closesAt: '2026-06-20T23:59:00Z',
    createdBy: 'Noticias 24/7 Radio',
    options: [
      { id: 'opt-017a', text: 'Entrevista con Leyendas del Deporte', votes: 567, percentage: 43.2 },
      { id: 'opt-017b', text: 'El Futuro de la Radio Digital', votes: 345, percentage: 26.3 },
      { id: 'opt-017c', text: 'Libertad de Expresión en Medios', votes: 234, percentage: 17.8 },
      { id: 'opt-017d', text: 'Panel de Cine Independiente', votes: 167, percentage: 12.7 },
    ],
    totalVotes: 1313,
    participants: 1313,
    comments: 345,
    shares: 123,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: 'opt-017a',
    tags: ['programa', 'entrevista', 'cerrada'],
  },
  {
    id: 'poll-018',
    question: '¿Qué beneficios nuevos querés para el Club VIP?',
    description: 'Los miembros VIP deciden los próximos beneficios. Podés elegir hasta 2 opciones. Los 2 más votados se implementan el próximo mes.',
    type: 'multiple',
    location: 'club',
    locationName: 'Club',
    entityName: 'Club VIP Banda Radio MX',
    entityId: 'club-012',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=100&height=100&seq=logo-banda-mx&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-20T09:00:00Z',
    closesAt: '2026-06-29T23:59:00Z',
    createdBy: 'Club VIP Banda Radio MX',
    options: [
      { id: 'opt-018a', text: 'Acceso Anticipado a Boletos', votes: 189, percentage: 56.1 },
      { id: 'opt-018b', text: 'Descuentos en Merch Oficial', votes: 167, percentage: 49.6 },
      { id: 'opt-018c', text: 'Sorteos Exclusivos Mensuales', votes: 145, percentage: 43.0 },
      { id: 'opt-018d', text: 'Contenido Behind the Scenes', votes: 123, percentage: 36.5 },
      { id: 'opt-018e', text: 'Grupo de WhatsApp VIP', votes: 98, percentage: 29.1 },
    ],
    totalVotes: 337,
    participants: 337,
    comments: 128,
    shares: 45,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['club', 'beneficios', 'vip'],
  },
  {
    id: 'poll-019',
    question: 'Votación rápida: ¿Qué emoji representa mejor tu estado de ánimo hoy?',
    description: 'Un emoji, un voto. Resultados en vivo en la cabina. La radio comenta los resultados al aire. ¡Rápido, solo 30 minutos!',
    type: 'quick',
    location: 'cabina',
    locationName: 'Cabina',
    entityName: 'Radio Fe y Esperanza',
    entityId: 'radio-87.00',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Faith%20based%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20dove%20and%20cross%20symbolism%2C%20warm%20golden%20tones%2C%20clean%20modern%20religious%20brand%20identity&width=100&height=100&seq=logo-fe-esperanza&orientation=squarish',
    status: 'closed',
    createdAt: '2026-06-20T08:00:00Z',
    closesAt: '2026-06-20T08:30:00Z',
    createdBy: 'Pastor Daniel Vega',
    options: [
      { id: 'opt-019a', text: '😊 Feliz', votes: 234, percentage: 38.9 },
      { id: 'opt-019b', text: '🙏 Agradecido', votes: 198, percentage: 32.9 },
      { id: 'opt-019c', text: '💪 Motivado', votes: 112, percentage: 18.6 },
      { id: 'opt-019d', text: '😴 Cansado', votes: 58, percentage: 9.6 },
    ],
    totalVotes: 602,
    participants: 602,
    comments: 89,
    shares: 23,
    score: { createPoll: 50, vote: 10, share: 15, comment: 5 },
    winnerId: 'opt-019a',
    tags: ['cabina', 'diversión', 'rápida'],
  },
  {
    id: 'poll-020',
    question: 'Ranking: Los programas más escuchados de la semana',
    description: 'Ordená los programas del 1 al 5 según cuáles escuchaste más esta semana. El ranking final define la programación destacada de la próxima semana.',
    type: 'ranking',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'Radio Internacional Voz',
    entityId: 'radio-140.00',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=International%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20sound%20waves%2C%20cosmopolitan%20tones%2C%20global%20broadcast%20brand%20identity&width=100&height=100&seq=logo-internacional&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-19T00:00:00Z',
    closesAt: '2026-06-27T23:59:00Z',
    createdBy: 'Radio Internacional Voz',
    options: [
      { id: 'opt-020a', text: 'Voces del Mundo', votes: 345, percentage: 78.9 },
      { id: 'opt-020b', text: 'Conexión Global', votes: 298, percentage: 68.2 },
      { id: 'opt-020c', text: 'Ritmos Sin Fronteras', votes: 267, percentage: 61.1 },
      { id: 'opt-020d', text: 'Noticiero Internacional', votes: 234, percentage: 53.5 },
      { id: 'opt-020e', text: 'Noches de Jazz', votes: 198, percentage: 45.3 },
    ],
    totalVotes: 437,
    participants: 437,
    comments: 167,
    shares: 78,
    score: { createPoll: 200, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['radio', 'ranking', 'programas'],
  },
  {
    id: 'poll-021',
    question: 'Eliminación: ¿Quién es el mejor fan de la radio?',
    description: 'Los 6 fans más activos compiten en eliminación directa. Cada 4 horas se elimina al que menos votos recibe. El ganador recibe una insignia de Fan Legendario.',
    type: 'elimination',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'La Mexicana FM',
    entityId: 'radio-103.50',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    status: 'closed',
    createdAt: '2026-06-14T10:00:00Z',
    closesAt: '2026-06-18T22:00:00Z',
    createdBy: 'La Mexicana FM',
    options: [
      { id: 'opt-021a', text: 'Carlos Mendoza', votes: 567, percentage: 42.1 },
      { id: 'opt-021b', text: 'María García', votes: 345, percentage: 25.6 },
      { id: 'opt-021c', text: 'José Hernández', votes: 234, percentage: 17.3 },
      { id: 'opt-021d', text: 'Ana Martínez', votes: 123, percentage: 9.1 },
      { id: 'opt-021e', text: 'Pedro López', votes: 56, percentage: 4.2 },
      { id: 'opt-021f', text: 'Laura Sánchez', votes: 24, percentage: 1.8 },
    ],
    totalVotes: 1349,
    participants: 1349,
    comments: 678,
    shares: 234,
    score: { createPoll: 250, vote: 15, share: 20, comment: 5 },
    winnerId: 'opt-021a',
    tags: ['radio', 'eliminación', 'fans'],
  },
  {
    id: 'poll-022',
    question: '¿Cuál es el mejor horario para el nuevo programa?',
    description: 'Ayudanos a elegir el horario perfecto para el nuevo programa que estamos preparando. ¡Tu voto cuenta!',
    type: 'single',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'Radio Comunitaria Unida',
    entityId: 'radio-87.02',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20people%20and%20heart%20motifs%2C%20warm%20earthy%20colors%2C%20inclusive%20grassroots%20brand%20identity&width=100&height=100&seq=logo-comunitaria&orientation=squarish',
    status: 'scheduled',
    createdAt: '2026-06-21T00:00:00Z',
    closesAt: '2026-07-01T00:00:00Z',
    createdBy: 'Radio Comunitaria Unida',
    options: [
      { id: 'opt-022a', text: 'Mañana (8 AM - 11 AM)', votes: 0, percentage: 0 },
      { id: 'opt-022b', text: 'Mediodía (12 PM - 3 PM)', votes: 0, percentage: 0 },
      { id: 'opt-022c', text: 'Tarde (4 PM - 7 PM)', votes: 0, percentage: 0 },
      { id: 'opt-022d', text: 'Noche (8 PM - 11 PM)', votes: 0, percentage: 0 },
    ],
    totalVotes: 0,
    participants: 0,
    comments: 0,
    shares: 0,
    score: { createPoll: 100, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['radio', 'programación', 'comunidad'],
  },
  {
    id: 'poll-023',
    question: '¿Qué premio preferirías ganar en un concurso?',
    description: 'Conocé las opciones de premios y votá por la que más te guste. Los resultados definen los premios de los próximos concursos.',
    type: 'single',
    location: 'evento',
    locationName: 'Evento',
    entityName: 'Sorteo Exclusivo Club',
    entityId: 'evt-015',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Cancun%20tropical%20beach%20resort%20giveaway%20concept%2C%20golden%20envelope%20with%20plane%20tickets%20on%20pristine%20white%20sand%2C%20turquoise%20ocean%20background%2C%20luxury%20vacation%20prize%20atmosphere&width=100&height=100&seq=logo-sorteo-cancun&orientation=squarish',
    status: 'closed',
    createdAt: '2026-06-05T10:00:00Z',
    closesAt: '2026-06-14T23:59:00Z',
    createdBy: 'La Mexicana FM',
    options: [
      { id: 'opt-023a', text: 'Viaje Todo Pagado', votes: 567, percentage: 38.9 },
      { id: 'opt-023b', text: 'Auriculares Profesionales', votes: 345, percentage: 23.7 },
      { id: 'opt-023c', text: 'Entradas VIP a Conciertos', votes: 298, percentage: 20.5 },
      { id: 'opt-023d', text: 'Merch Oficial de la Radio', votes: 156, percentage: 10.7 },
      { id: 'opt-023e', text: 'Mención al Aire + Insignia', votes: 91, percentage: 6.2 },
    ],
    totalVotes: 1457,
    participants: 1457,
    comments: 234,
    shares: 89,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: 'opt-023a',
    tags: ['evento', 'premios', 'cerrada'],
  },
  {
    id: 'poll-024',
    question: 'Múltiple: ¿Qué actividades te gustan más de la radio?',
    description: 'Seleccioná todas las actividades que más disfrutás de nuestra radio. Nos ayuda a mejorar tu experiencia.',
    type: 'multiple',
    location: 'radio',
    locationName: 'Radio',
    entityName: 'La Mexicana FM',
    entityId: 'radio-103.50',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-17T08:00:00Z',
    closesAt: '2026-06-30T23:59:00Z',
    createdBy: 'La Mexicana FM',
    options: [
      { id: 'opt-024a', text: 'Escuchar Música en Vivo', votes: 678, percentage: 82.4 },
      { id: 'opt-024b', text: 'Participar en Concursos', votes: 567, percentage: 68.9 },
      { id: 'opt-024c', text: 'Chatear en la Cabina', votes: 498, percentage: 60.5 },
      { id: 'opt-024d', text: 'Enviar Saludos al Aire', votes: 423, percentage: 51.4 },
      { id: 'opt-024e', text: 'Pedir Canciones', votes: 398, percentage: 48.4 },
      { id: 'opt-024f', text: 'Seguir a los Locutores', votes: 312, percentage: 37.9 },
    ],
    totalVotes: 823,
    participants: 823,
    comments: 198,
    shares: 78,
    score: { createPoll: 200, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['radio', 'comunidad', 'feedback'],
  },
  {
    id: 'poll-025',
    question: '¿Quién debería ser el próximo invitado especial del programa?',
    description: 'Los oyentes eligen al próximo invitado. El más votado será contactado para una entrevista exclusiva en vivo.',
    type: 'single',
    location: 'programa',
    locationName: 'Programa',
    entityName: 'Mañanas Argentinas',
    entityId: 'program-mañanas-argentinas',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Morning%20show%20program%20logo%20design%2C%20circular%20emblem%20with%20sun%20and%20coffee%20motifs%2C%20warm%20energetic%20colors%2C%20friendly%20morning%20broadcast%20identity&width=100&height=100&seq=logo-mañanas-argentinas&orientation=squarish',
    status: 'active',
    createdAt: '2026-06-21T08:00:00Z',
    closesAt: '2026-06-25T12:00:00Z',
    createdBy: 'Mañanas Argentinas',
    options: [
      { id: 'opt-025a', text: 'Valentina Ríos - Locutora #1', votes: 234, percentage: 35.1 },
      { id: 'opt-025b', text: 'Diego Montenegro - Comediante', votes: 189, percentage: 28.3 },
      { id: 'opt-025c', text: 'Camila Ortega - Cantante', votes: 145, percentage: 21.7 },
      { id: 'opt-025d', text: 'Roberto Aguilar - Periodista', votes: 99, percentage: 14.8 },
    ],
    totalVotes: 667,
    participants: 667,
    comments: 123,
    shares: 45,
    score: { createPoll: 150, vote: 10, share: 15, comment: 5 },
    winnerId: null,
    tags: ['programa', 'invitado', 'comunidad'],
  },
];

// ─── Helpers ────────────────────────────────────────────

export function getAllPolls(): Poll[] {
  return rfwPolls;
}

export function getPollsByType(type: PollType | 'todos'): Poll[] {
  if (type === 'todos') return rfwPolls;
  return rfwPolls.filter((p) => p.type === type);
}

export function getPollsByLocation(location: PollLocation | 'todos'): Poll[] {
  if (location === 'todos') return rfwPolls;
  return rfwPolls.filter((p) => p.location === location);
}

export function getPollsByStatus(status: PollStatus | 'todos'): Poll[] {
  if (status === 'todos') return rfwPolls;
  return rfwPolls.filter((p) => p.status === status);
}

export function getActivePolls(): Poll[] {
  return rfwPolls.filter((p) => p.status === 'active');
}

export function getPopularPolls(): Poll[] {
  return [...rfwPolls].filter((p) => p.totalVotes > 0).sort((a, b) => b.totalVotes - a.totalVotes).slice(0, 8);
}

export function getCabinaPolls(): Poll[] {
  return rfwPolls.filter((p) => p.location === 'cabina' && p.status === 'active');
}

export function getPollById(id: string): Poll | undefined {
  return rfwPolls.find((p) => p.id === id);
}

export function getClosingSoonPolls(): Poll[] {
  return rfwPolls
    .filter((p) => p.status === 'active')
    .sort((a, b) => new Date(a.closesAt).getTime() - new Date(b.closesAt).getTime())
    .slice(0, 5);
}

export function formatPollDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatPollDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

export function formatCount(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function getTimeRemainingText(closesAt: string): string {
  const now = new Date();
  const target = new Date(closesAt);
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return 'Cerrada';
  const diffMin = Math.ceil(diffMs / (1000 * 60));
  const diffHrs = Math.ceil(diffMs / (1000 * 60 * 60));
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffMin < 60) return `${diffMin} min`;
  if (diffHrs < 24) return `${diffHrs}h`;
  if (diffDays === 1) return 'Mañana';
  if (diffDays <= 3) return `${diffDays} días`;
  return formatPollDateShort(closesAt);
}

export function getWinnerText(poll: Poll): string {
  if (!poll.winnerId) return '';
  const winner = poll.options.find((o) => o.id === poll.winnerId);
  return winner ? winner.text : '';
}